import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffBroadcastService,
  BroadcastListItem,
  BroadcastStat,
  BroadcastMediaItem,
  BroadcastReceiverDetail,
} from '../services/staff-broadcast.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

interface BroadcastReceiverOption {
  receiverId: number;
  receiver: string;
}

interface BroadcastStatusFilterOption {
  label: string;
  receiverType: string;
}

interface BroadcastCardItem {
  id: string;
  subject: string;
  message: string;
  audience: string;
  isRead: boolean;
  views: number;
  expectedViews: number;
  readPercentage: number;
  sentOnDate: string;
  sentOnTime: string;
  attachmentCount: number;
  // Kept verbatim from the list response so the Details sheet never
  // re-fetches — everything it shows (subject, description, meta,
  // attachments + URLs) is already here, per spec.
  media: BroadcastMediaItem[];
}

const MAX_ATTACHMENTS = 5;
const PAGE_LIMIT = 10;
const VIEWERS_PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

@Component({
  selector: 'app-staff-broadcast',
  templateUrl: './staff-broadcast.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-broadcast.component.css']
})
export class StaffBroadcastComponent implements OnInit, OnDestroy {
  isSuperAdmin = false;
  createBroadcastEnabled = false;

  // ---------- Overview list — same table + filters + paginator design as
  // web admin's broadcast-v2 (BroadcastOverview table, not the mobile
  // app's card-list/pills/infinite-scroll). ----------
  broadcasts: BroadcastCardItem[] = [];
  stats: BroadcastStat[] = [];
  searchTerm = '';
  private lastSearchedTerm = '';
  private searchDebounceTimer: any;

  receiverOptions: BroadcastReceiverOption[] = [];
  statusFilterOptions: BroadcastStatusFilterOption[] = [];
  selectedStatusLabel: string | null = null;

  selectedDateFilterKey = 'All time';
  selectedDate: Date | null = null;
  customDateLabel = '';
  isChooseDateOpen = false;

  currentPage = 1;
  pageSize = PAGE_LIMIT;
  totalRecords = 0;
  isLoadingBroadcasts = true;
  readonly skeletonRows = Array.from({ length: 5 }, () => ({}));
  readonly skeletonStatCards = Array.from({ length: 4 }, () => ({}));

  get paginatorFirst(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  // ---------- Create composer ----------
  showComposer = false;
  loadingReceivers = false;
  selectedReceiverId: number | null = null;
  newTitle = '';
  newMessage = '';
  attachments: File[] = [];
  submitting = false;
  readonly maxAttachments = MAX_ATTACHMENTS;

  constructor(
    private staffBroadcastService: StaffBroadcastService,
    private staffSession: StaffSessionService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const info = this.staffSession.getSchoolAndStaffInfo();
    const staffDetails = this.staffSession.getStaffDetails();
    this.isSuperAdmin = !!(info?.isSuperAdmin ?? staffDetails?.isSuperAdmin);
    // Two independent gates: role AND the feature flag — ANDed, not either/or.
    this.createBroadcastEnabled = this.staffSession.isFeatureEnabled('CREATE_BROADCAST');
    this.loadReceiverOptions();
    this.loadBroadcasts(1);
  }

  get canCreateBroadcast(): boolean {
    return this.isSuperAdmin && this.createBroadcastEnabled;
  }

  private get academicId(): number | string {
    const info = this.staffSession.getSchoolAndStaffInfo();
    return info?.pkAcademicId ?? '';
  }

  // Same icon/color-by-slug mapping as web admin's broadcast-v2
  // (getStatIcon/getStatIconClass), translated to the Remix Icon set staff
  // pages use elsewhere instead of Phosphor.
  getStatIcon(slug: string | undefined): string {
    switch (slug) {
      case 'total-broadcast': return 'ri-send-plane-fill';
      case 'all': return 'ri-global-line';
      case 'parents': return 'ri-group-fill';
      case 'staffs': return 'ri-user-fill';
      default: return 'ri-bar-chart-fill';
    }
  }

  getStatIconClass(slug: string | undefined): string {
    switch (slug) {
      case 'total-broadcast': return 'sb-icon-blue';
      case 'all': return 'sb-icon-purple';
      case 'parents': return 'sb-icon-green';
      case 'staffs': return 'sb-icon-orange';
      default: return 'sb-icon-blue';
    }
  }

  // subject falls back to broadcastMessage if no subject was set; attachment
  // count is surfaced (not real filenames — the API never returns those).
  private mapToCardItem(item: BroadcastListItem): BroadcastCardItem {
    return {
      id: item.broadcastId,
      subject: item.broadcastSubject || item.broadcastMessage,
      message: item.broadcastMessage,
      audience: item.broadcastReceiver,
      isRead: item.isRead,
      views: item.views,
      expectedViews: item.expectedViews,
      readPercentage: item.readPercentage,
      sentOnDate: item.sentOnDate,
      sentOnTime: item.sentOnTime,
      attachmentCount: item.broadcastMediaList?.length || 0,
      media: item.broadcastMediaList || [],
    };
  }

  private formatDateYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getDateRangeForFilter(): { startDate: string | null; endDate: string | null } {
    const today = new Date();
    if (this.selectedDateFilterKey === 'Last 30 days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { startDate: this.formatDateYYYYMMDD(start), endDate: this.formatDateYYYYMMDD(today) };
    }
    if (this.selectedDateFilterKey === 'Last 7 days') {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { startDate: this.formatDateYYYYMMDD(start), endDate: this.formatDateYYYYMMDD(today) };
    }
    if (this.selectedDate) {
      const formatted = this.formatDateYYYYMMDD(this.selectedDate);
      return { startDate: formatted, endDate: formatted };
    }
    return { startDate: null, endDate: null };
  }

  private getReceiverTypeForFilter(): string | null {
    const match = this.statusFilterOptions.find(option => option.label === this.selectedStatusLabel);
    return match ? match.receiverType : null;
  }

  async loadBroadcasts(page: number) {
    this.isLoadingBroadcasts = true;
    try {
      const { startDate, endDate } = this.getDateRangeForFilter();
      const response = await firstValueFrom(this.staffBroadcastService.getBroadcastMessagesForStaff({
        academicId: this.academicId,
        receiverType: this.getReceiverTypeForFilter(),
        search: this.searchTerm?.trim() || null,
        startDate,
        endDate,
        readStatus: null,
        pageNumber: page,
        pageLimit: this.pageSize,
      }));
      if (response?.status && response.body) {
        this.broadcasts = (response.body.broadcastDetail || []).map(item => this.mapToCardItem(item));
        this.stats = response.body.stats || [];
        this.currentPage = page;
        const maxPageNumber = response.body.maxPageNumber || 1;
        const totalFromStats = this.stats.find(s => s.slug === 'total-broadcast')?.value ?? this.broadcasts.length;
        this.totalRecords = typeof totalFromStats === 'number' ? totalFromStats : maxPageNumber * this.pageSize;
      } else {
        this.broadcasts = [];
        const message = await firstValueFrom(this.translate.get('bv2BroadcastsFetchError'));
        customToastr(response?.message || message, 'error');
      }
    } catch {
      this.broadcasts = [];
      const message = await firstValueFrom(this.translate.get('bv2BroadcastsFetchException'));
      customToastr(message, 'error');
    } finally {
      this.isLoadingBroadcasts = false;
    }
  }

  onPaginatorPageChange(event: any): void {
    this.pageSize = event.rows;
    this.loadBroadcasts(event.page + 1);
  }

  selectStatus(status: string): void {
    this.selectedStatusLabel = status;
    this.loadBroadcasts(1);
  }

  onDateFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedDateFilterKey = value;
    if (value === 'Choose date') {
      this.isChooseDateOpen = true;
      return;
    }
    this.isChooseDateOpen = false;
    if (value === 'All time') {
      this.selectedDate = null;
      this.customDateLabel = '';
    }
    this.loadBroadcasts(1);
  }

  onCalendarDateSelected(date: Date): void {
    if (!date) { return; }
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    this.customDateLabel = formatter.format(date);
    this.selectedDateFilterKey = this.customDateLabel;
    this.isChooseDateOpen = false;
    this.loadBroadcasts(1);
  }

  onSearchChange(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      const trimmed = this.searchTerm?.trim() || '';
      if (trimmed === this.lastSearchedTerm) { return; }
      this.lastSearchedTerm = trimmed;
      this.loadBroadcasts(1);
    }, SEARCH_DEBOUNCE_MS);
  }

  // ---------- Broadcast details sheet ----------
  // Opening the sheet never fetches the broadcast itself — item is already
  // the fully-mapped card object from the list response, so the header
  // (subject, description, meta, attachments + URLs) renders instantly.
  // Only "Who viewed and when" (Super Admin only) is a fresh network call.
  showDetails = false;
  selectedBroadcast: BroadcastCardItem | null = null;
  viewers: BroadcastReceiverDetail[] = [];
  viewersPageNumber = 1;
  viewersMaxPageNumber = 1;
  loadingViewers = false;
  loadingMoreViewers = false;
  viewersSearchOpen = false;
  viewersSearchTerm = '';
  private viewersSearchDebounceTimer: any;

  openBroadcast(item: BroadcastCardItem) {
    this.selectedBroadcast = item;
    this.showDetails = true;
    this.lockPageScroll();
    this.viewers = [];
    this.viewersPageNumber = 1;
    this.viewersMaxPageNumber = 1;
    this.viewersSearchOpen = false;
    this.viewersSearchTerm = '';

    // Optimistically flips isRead locally, then fires the mark-read call —
    // fire-and-forget by design (matches the mobile app's own behavior per
    // spec: no await, no error reconciliation if the request fails).
    if (!item.isRead) {
      item.isRead = true;
      this.staffBroadcastService.markBroadcastReadForStaff(item.id).subscribe();
    }

    if (this.isSuperAdmin) {
      this.loadingViewers = true;
      this.fetchViewers(1);
    }
  }

  closeDetails() {
    this.showDetails = false;
    this.selectedBroadcast = null;
    this.unlockPageScrollIfNoDrawerOpen();
  }

  private async fetchViewers(page: number) {
    if (!this.selectedBroadcast) { return; }
    if (page === 1) {
      this.loadingViewers = true;
    } else {
      this.loadingMoreViewers = true;
    }
    try {
      const response = await firstValueFrom(this.staffBroadcastService.getBroadcastReceiverDetailList({
        broadcastId: this.selectedBroadcast.id,
        pageNumber: page,
        pageLimit: VIEWERS_PAGE_LIMIT,
        search: this.viewersSearchTerm.trim() || null,
      }));
      if (response?.status && response.body) {
        const list = response.body.receiverDetailList || [];
        this.viewers = page === 1 ? list : [...this.viewers, ...list];
        this.viewersMaxPageNumber = response.body.maxPageNumber || 1;
        this.viewersPageNumber = page;
      } else if (page === 1) {
        this.viewers = [];
      }
    } catch {
      if (page === 1) { this.viewers = []; }
      const message = await firstValueFrom(this.translate.get('bv2ViewersFetchException'));
      customToastr(message, 'error');
    } finally {
      this.loadingViewers = false;
      this.loadingMoreViewers = false;
    }
  }

  loadMoreViewers() {
    if (this.viewersPageNumber < this.viewersMaxPageNumber && !this.loadingMoreViewers && !this.loadingViewers) {
      this.fetchViewers(this.viewersPageNumber + 1);
    }
  }

  toggleViewersSearch() {
    this.viewersSearchOpen = !this.viewersSearchOpen;
    if (!this.viewersSearchOpen && this.viewersSearchTerm) {
      this.viewersSearchTerm = '';
      this.fetchViewers(1);
    }
  }

  onViewersSearchInput(value: string) {
    this.viewersSearchTerm = value;
    clearTimeout(this.viewersSearchDebounceTimer);
    this.viewersSearchDebounceTimer = setTimeout(() => this.fetchViewers(1), SEARCH_DEBOUNCE_MS);
  }

  // Colored initials circle when there's no photo — color cycles by row
  // index, matching the mobile app's blue/pink/green rotation.
  private readonly avatarColors = ['sb-avatar-blue', 'sb-avatar-pink', 'sb-avatar-green'];
  avatarColor(index: number): string {
    return this.avatarColors[index % this.avatarColors.length];
  }

  getInitials(name: string): string {
    if (!name) { return ''; }
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
  }

  isImageMedia(mediaType: string): boolean {
    return mediaType?.toUpperCase() === 'IMAGE';
  }

  // In-app media preview overlay — same pattern as web admin's
  // broadcast-v2 (openMediaPreview/closeMediaPreview): images render
  // directly, documents render in a sandboxed iframe via a sanitized URL,
  // rather than opening the attachment in a new browser tab.
  isMediaPreviewOpen = false;
  previewMedia: BroadcastMediaItem | null = null;
  previewMediaSafeUrl: SafeResourceUrl | null = null;

  openMediaPreview(media: BroadcastMediaItem) {
    this.previewMedia = media;
    this.previewMediaSafeUrl = this.isImageMedia(media.broadcastMediaType)
      ? null
      : this.sanitizer.bypassSecurityTrustResourceUrl(media.broadcastMediaUrl);
    this.isMediaPreviewOpen = true;
  }

  closeMediaPreview() {
    this.isMediaPreviewOpen = false;
    this.previewMedia = null;
    this.previewMediaSafeUrl = null;
  }

  // ---------- Create composer ----------

  openComposer() {
    this.resetComposerForm();
    this.showComposer = true;
    this.lockPageScroll();
    if (!this.receiverOptions.length && !this.loadingReceivers) {
      this.loadReceiverOptions();
    }
  }

  closeComposer() {
    if (this.submitting) { return; }
    this.showComposer = false;
    this.unlockPageScrollIfNoDrawerOpen();
  }

  private resetComposerForm() {
    this.selectedReceiverId = null;
    this.newTitle = '';
    this.newMessage = '';
    this.attachments = [];
  }

  private async loadReceiverOptions() {
    this.loadingReceivers = true;
    try {
      const response = await firstValueFrom(this.staffBroadcastService.getBroadcastType(this.academicId));
      const data = response as any;
      this.receiverOptions = data?.status ? (data.body?.broadcastReceiverData || []) : [];
      const sentToPrefix = await firstValueFrom(this.translate.get('bv2SentToPrefix'));
      this.statusFilterOptions = this.receiverOptions.map(option => ({
        label: `${sentToPrefix}${option.receiver}`,
        receiverType: option.receiver,
      }));
    } catch {
      this.receiverOptions = [];
      this.statusFilterOptions = [];
    } finally {
      this.loadingReceivers = false;
    }
  }

  selectReceiver(receiverId: number) {
    this.selectedReceiverId = receiverId;
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) { return; }
    const remainingSlots = this.maxAttachments - this.attachments.length;
    if (remainingSlots <= 0) {
      this.notifyAttachmentLimit();
      return;
    }
    if (files.length > remainingSlots) {
      this.notifyAttachmentLimit();
    }
    this.attachments = [...this.attachments, ...files.slice(0, remainingSlots)];
  }

  removeAttachment(index: number) {
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  private async notifyAttachmentLimit() {
    const message = await firstValueFrom(this.translate.get('bv2AttachmentLimitMessage', { count: this.maxAttachments }));
    customToastr(message, 'error');
  }

  async submitBroadcast() {
    if (!this.selectedReceiverId || !this.newTitle.trim() || !this.newMessage.trim()) {
      const message = await firstValueFrom(this.translate.get('pleaseFillDetailsErrorMessage'));
      customToastr(message, 'error');
      return;
    }

    this.submitting = true;
    try {
      const body = {
        academicId: String(this.academicId),
        broadcastReceiverId: String(this.selectedReceiverId),
        isHighPriority: 'false',
        broadcastMessage: this.newMessage.trim(),
        broadcastSubject: this.newTitle.trim(),
      };
      const media = this.attachments.length ? this.attachments : null;
      const response = await firstValueFrom(this.staffBroadcastService.createBroadcastService(body, media));
      const data = response as any;
      if (data?.status) {
        // Close/refresh first — a toastr DOM error must never block the
        // drawer from closing or the list from refreshing after a
        // confirmed-successful create.
        this.showComposer = false;
        this.unlockPageScrollIfNoDrawerOpen();
        this.loadBroadcasts(1);
        const message = await firstValueFrom(this.translate.get('createBroadcastSuccessMessage'));
        customToastr(message, 'success');
      } else {
        const message = await firstValueFrom(this.translate.get('BroadcastFailedToCreateErrorText'));
        customToastr(data?.message || message, 'error');
      }
    } catch (error: any) {
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(error?.error?.message || message, 'error');
    } finally {
      this.submitting = false;
    }
  }

  // The sidepopovers are full-height overlays — with the page behind them
  // still scrollable, scrolling inside the drawer body also scrolls the
  // page underneath. Lock body scroll while either drawer is open, only
  // restoring it once neither is left open (both can't overlap today, but
  // this stays correct if that ever changes).
  private lockPageScroll() {
    document.body.style.overflow = 'hidden';
  }

  private unlockPageScrollIfNoDrawerOpen() {
    if (!this.showComposer && !this.showDetails) {
      document.body.style.overflow = '';
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }
}
